<script setup lang="ts">
import type { CheckboxItem } from '@/models/checkbox-item';
import { computed, ref } from 'vue';

const props = defineProps<{
  items: CheckboxItem[];
  selected: string[];
}>();

const emit = defineEmits(['update:selected']);

const selectedItems = ref(props.selected);
const selectedHandler = () => emit('update:selected', selectedItems.value);
const selectAll = (event: Event) => {
  selectedItems.value = [];
  if ((event.target as HTMLInputElement).checked) {
    props.items.map((item) => selectedItems.value.push(item.id));
  }
  emit('update:selected', selectedItems.value);
};

const splitIndex = computed(() => Math.ceil(props.items.length / 2));
const firstColumnItems = computed(() => props.items.slice(0, splitIndex.value));
const secondColumnItems = computed(() => props.items.slice(splitIndex.value));
</script>

<template>
  <div class="container">
    <div class="checkbox-input">
      <input type="checkbox" v-on:change="selectAll($event)" :checked="selectedItems.length === items.length" />
      <label class="checkbox-label bold-text"> {{ $t('global.select_all_button_filter') }} </label>
    </div>
    <div class="divider"></div>
    <div v-if="splitIndex > 5" class="split-container">
      <div class="column">
        <ul class="view-items checkbox-list">
          <li v-for="(item, index) in firstColumnItems" :key="index" class="checkbox-list-item">
            <label class="checkbox-input" :class="{ disabled: !item.selectable }">
              <input
                type="checkbox"
                :value="item.id"
                :disabled="!item.selectable"
                v-model="selectedItems"
                v-on:change="selectedHandler()"
                :checked="item.checked" />
              <div class="checkbox-text">
                <span class="checkbox-label"> {{ item.name }}</span>
                <div v-if="item.notes" class="notes">
                  <span v-for="note in item.notes" :key="item.notes.indexOf(note)" data-cy="item-note">
                    {{ note }}
                  </span>
                </div>
              </div>
            </label>
          </li>
        </ul>
      </div>
      <div class="column">
        <ul>
          <li v-for="(item, index) in secondColumnItems" :key="index" class="checkbox-list-item">
            <label class="checkbox-input" :class="{ disabled: !item.selectable }">
              <input
                type="checkbox"
                :value="item.id"
                :disabled="!item.selectable"
                v-model="selectedItems"
                v-on:change="selectedHandler()"
                :checked="item.checked" />
              <div class="checkbox-text">
                <span class="checkbox-label"> {{ item.name }}</span>
                <div v-if="item.notes" class="notes">
                  <span v-for="note in item.notes" :key="item.notes.indexOf(note)" data-cy="item-note">
                    {{ note }}
                  </span>
                </div>
              </div>
            </label>
          </li>
        </ul>
      </div>
    </div>
    <div v-else>
      <div class="column">
        <ul class="view-items checkbox-list">
          <li v-for="item in items" :key="item.id" class="checkbox-list-item">
            <div class="checkbox-input" :class="{ disabled: !item.selectable }">
              <input
                type="checkbox"
                :value="item.id"
                :disabled="!item.selectable"
                v-model="selectedItems"
                v-on:change="selectedHandler()"
                :checked="item.checked" />
              <div class="checkbox-text">
                <label class="checkbox-label"> {{ item.name }}</label>
                <div v-if="item.notes" class="notes">
                  <label v-for="note in item.notes" :key="item.notes.indexOf(note)" data-cy="item-note">
                    {{ note }}
                  </label>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@mixin flex-cnt {
  display: flex;
  gap: 12px;
}

.column {
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* Two columns with equal width */
  grid-auto-flow: column; /* Set to display items in columns */
  grid-gap: 10px; /* Gap between grid items */
  align-items: flex-start;
}

.container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.split-container {
  display: flex;
}

.disabled {
  color: #888;
}

.view-items {
  max-width: fit-content;
}

.divider {
  color: var(--shade-1);
  border-top: 1px solid #ccc;
  margin: 20px 0;
  width: 150%;
}

.checkbox-list {
  list-style: none;
  padding: 0;
}

.checkbox-list-item {
  display: flex;
  margin-bottom: 10px;
}

.checkbox-text {
  display: flex;
  flex-direction: column;
  margin-left: 5px;
  width: fit-content;

  .checkbox-label {
    display: inline-block;
  }

  .notes {
    display: flex;
    flex-direction: column;
    width: fit-content;
    color: var(--primary-label-input-color);
  }
}

.checkbox-input {
  @include flex-cnt;
  display: flex;
  justify-content: center;
  margin-top: 20px;
  max-width: 820px;
  width: max-content;

  .bold-text {
    font-weight: bold;
  }

  label {
    text-align: left;
    font-size: 14px;
    font-weight: 400;
  }

  input[type='checkbox'] {
    appearance: none;
    background-color: #fff;
    margin: 0;
    padding: 0 4px;
    width: 15px;
    height: 15px;
    border: 1px solid #000000;
    border-radius: 2px;
    display: grid;
    place-content: center;
  }

  input[type='checkbox']::before {
    content: '';
    width: 9px;
    height: 9px;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    background-color: #000000;
    border-radius: 2px;
  }

  input[type='checkbox']:checked::before {
    transform: scale(1);
  }
}
</style>
